import ReactECharts from 'echarts-for-react';

/**
 * KernelVersionChart — GPT-4 behavior drift, March 2023 → June 2023 (blog05).
 * Component name kept for import compatibility; the content is now the real
 * measured drift from Chen, Zaharia & Zou, "How Is ChatGPT's Behavior Changing
 * over Time?", Harvard Data Science Review 6(2), 2024 — arXiv:2307.09009v3,
 * https://arxiv.org/abs/2307.09009
 *
 * Data (mirrors public/blog/gpt4-drift-chen-2023.csv), GPT-4 rows:
 *   prime_vs_composite    84.0% → 51.1% (accuracy; n=1000; v3 methodology:
 *                         500 primes + 500 composites — v1's 97.6%→2.4% figures
 *                         were revised by the authors)
 *   happy_number_counting 83.6% → 35.2% (accuracy; n=500)
 *   code_generation       52.0% → 10.0% (directly executable; n=50)
 *   sensitive_questions   21.0% →  5.0% (response rate; n=100)
 *
 * Chart-type-first: paired values across tasks → ECharts native grouped bar.
 * Brand colors; opaque white background.
 */

const BRAND = {
  teal: '#0AB3BF',
  slate: '#334155',
  slateBar: '#94A3B8', // March bars — muted baseline
  muted: '#64748B',
  surface: '#FFFFFF',
};

const TASKS = [
  { label: 'Prime vs composite', metric: 'accuracy', n: 1000, march: 84.0, june: 51.1 },
  { label: 'Happy-number counting', metric: 'accuracy', n: 500, march: 83.6, june: 35.2 },
  { label: 'Code generation', metric: 'directly executable', n: 50, march: 52.0, june: 10.0 },
  { label: 'Sensitive questions', metric: 'response rate', n: 100, march: 21.0, june: 5.0 },
];

/** ONE shared percent formatter — tooltip, axis ticks, and bar labels all use it. */
const formatPercent = (v: number) => `${v}%`;

export const KernelVersionChart = ({ height = 320 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const t = TASKS[params[0].dataIndex];
        const rows = params.map((p) => `${p.marker} ${p.seriesName}: <b>${formatPercent(p.value)}</b>`).join('<br/>');
        return `${t.label} — GPT-4 ${t.metric} (n=${t.n})<br/>${rows}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 13 },
      data: ['March 2023', 'June 2023'],
    },
    grid: { left: 48, right: 24, top: 48, bottom: 60 },
    xAxis: {
      type: 'category',
      data: TASKS.map((t) => t.label),
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.muted, fontSize: 12, fontWeight: 600, interval: 0, width: 120, overflow: 'break' },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: 'GPT-4 (%)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatPercent(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.35 } },
    },
    series: [
      {
        name: 'March 2023',
        type: 'bar',
        barGap: '12%',
        itemStyle: { color: BRAND.slateBar, borderRadius: [3, 3, 0, 0] },
        data: TASKS.map((t) => t.march),
        label: {
          show: true,
          position: 'top',
          color: BRAND.muted,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPercent(p.value),
        },
      },
      {
        name: 'June 2023',
        type: 'bar',
        itemStyle: { color: BRAND.teal, borderRadius: [3, 3, 0, 0] },
        data: TASKS.map((t) => t.june),
        label: {
          show: true,
          position: 'top',
          color: BRAND.slate,
          fontSize: 11,
          fontWeight: 700,
          formatter: (p: { value: number }) => formatPercent(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. Same model name, different behavior, three months apart. Without a versioned contract, your
        agent&apos;s behavior drifts silently. (Chen, Zaharia &amp; Zou 2023, arXiv:2307.09009v3)
      </p>
      <p className="mt-1 text-label-xs text-slate-600 dark:text-slate-500 text-center">
        v3 methodology (500 primes + 500 composites); v1&apos;s 97.6%→2.4% figures were revised by the authors.
        &ldquo;Sensitive questions&rdquo; is a response rate, not an accuracy.
      </p>
    </div>
  );
};
